import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import UserAvatar from './UserAvatar';
import InlineSelect from './InlineSelect';
import InlineIndustryInput from './InlineIndustryInput';

const STATUS_COLORS = {
  'EN CURSO':   'en-curso',
  'COMPLETADO': 'completado',
  'PLANEADO':   'planeado',
  'BLOQUEADO':  'bloqueado',
  'PAUSA':      'pausa',
};

const PRODUCT_OPTS = ['facturacion','OTC','terminales','links de pago','wallet','software','Telefonica'];

const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: '2-digit' })
  : '—';

// ── Column widths ─────────────────────────────────────────────────────────────
const COL = {
  check:    '32px',
  product: '100px',
  industry:'120px',
  fecha:    '72px',
  status:  '100px',
  cliente:  '140px',
  trans:    'minmax(250px, 1fr)',
  oper:     '120px',
  borrar:   '40px',
};
// Exportado -- `AddClientWizardRow` (renderizado por el container como `wizardRow`) necesita la
// misma plantilla de columnas para que su fila quede alineada con el resto de la tabla.
export const GRID_COLS = `${COL.check} ${COL.product} ${COL.industry} ${COL.fecha} ${COL.status} ${COL.cliente} ${COL.trans} ${COL.oper} ${COL.borrar}`;

// Extraído de ClientsView.jsx (02.3-client.md paso b) -- la tabla del pipeline (no-Cosmic):
// header, botón de agregar, fila del wizard (recibida ya renderizada, ver `wizardRow`) y las
// filas de datos. Presentacional: `onUpdateProduct`/`onUpdateIndustry`/`onArchive` reemplazan
// las llamadas directas a `updateOpMutation`/`toast.prompt` que vivían acá -- el container
// decide cómo confirmar el archivado (hoy vía `toast.prompt`), esta hoja solo dispara el intent.
export default function ClientOpsTable({
  userHasAccessToTab,
  loading,
  ops,
  search,
  existingIndustries,
  onOpenPanel,
  onUpdateProduct,
  onUpdateIndustry,
  onArchive,
  onAddClick,
  wizardRow,
}) {
  const { t } = useTranslation('client');

  return (
    <div className="client-table">
      {/* Header row */}
      <div className="client-table-header" style={{ display:'grid', gridTemplateColumns: GRID_COLS, gap: 0 }}>
        <div className="client-th" style={{ width: COL.check }}></div>
        <div className="client-th">{t('table.columns.producto')}</div>
        <div className="client-th">{t('table.columns.industria')}</div>
        <div className="client-th">{t('table.columns.fecha')}</div>
        <div className="client-th">{t('table.columns.estatus')}</div>
        <div className="client-th">{t('table.columns.cliente')}</div>
        <div className="client-th">{t('table.columns.transaccion')}</div>
        <div className="client-th">{t('table.columns.oper')}</div>
        <div className="client-th" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Trash2 size={13} style={{ opacity: 0.5 }} /></div>
      </div>

      {/* Add button */}
      <div className="client-add-btn-row">
        <button className="client-add-btn" onClick={onAddClick}>
          <Plus size={13} /> {t('table.addButton')}
        </button>
      </div>

      {wizardRow}

      {/* Rows */}
      {!userHasAccessToTab ? (
        <div className="client-empty" style={{color:'#ef4444'}}>{t('table.noPermission')}</div>
      ) : loading ? (
        <div className="client-empty">{t('table.loading')}</div>
      ) : ops.length === 0 ? (
        <div className="client-empty">{search ? t('table.emptyNoOpsFor', { search }) : t('table.emptyNoOps')}</div>
      ) : (
        ops.map(op => {
          const badgeClass = STATUS_COLORS[op.status] || 'planeado';
          return (
            <div key={op.id}
              className="client-table-row"
              style={{ display:'grid', gridTemplateColumns: GRID_COLS, gap: 0 }}
              onClick={() => onOpenPanel(op)}
            >
              {/* Checkbox */}
              <div className="client-cell" style={{ display:'flex', alignItems:'center' }}
                onClick={e => e.stopPropagation()}>
                <input type="checkbox" className="client-checkbox" aria-label={t('table.selectRowAriaLabel', { name: op.transaction || t('table.operationFallback') })} />
              </div>

              {/* Producto */}
              <div className="client-cell" style={{padding:'2px var(--space-1)'}} onClick={e=>e.stopPropagation()}>
                {op.product === 'terminales' ? (
                  <span className="client-badge" style={{
                    backgroundColor: '#f9731615',
                    color: '#f97316',
                    borderColor: '#f97316',
                    border: '1px solid #f9731644',
                    padding: '2px var(--space-2)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '10px',
                    fontWeight: '600'
                  }}>
                    terminales
                  </span>
                ) : (
                  <InlineSelect value={op.product} options={PRODUCT_OPTS} isProducto={true}
                    onSave={v => onUpdateProduct(op, v)} />
                )}
              </div>

              {/* Industria */}
              <div className="client-cell" style={{padding:'2px var(--space-1)'}} onClick={e=>e.stopPropagation()}>
                <InlineIndustryInput
                  value={op.industry}
                  options={existingIndustries}
                  onSave={v => onUpdateIndustry(op, v)}
                />
              </div>

              {/* Fecha */}
              <div className="client-cell client-cell-date">{fmtDate(op.operation_date || op.created_at)}</div>

              {/* Estatus */}
              <div className="client-cell">
                <span className={`client-badge ${badgeClass}`}>{op.status}</span>
              </div>

              {/* Cliente */}
              <div className="client-cell">
                {op.client_id
                  ? <UserAvatar img={op.cliente_img} nick={op.cliente_nick} />
                  : <span style={{color:'rgba(255,255,255,0.2)', fontSize:11}}>—</span>}
              </div>

              {/* Transacción */}
              <div className="client-cell" title={op.transaction}>
                {op.transaction || '—'}
              </div>

              {/* Oper */}
              <div className="client-cell">
                {op.oper_id
                  ? <UserAvatar img={op.oper_img} nick={op.oper_nick} />
                  : <span style={{color:'rgba(255,255,255,0.2)', fontSize:11}}>—</span>}
              </div>

              {/* Borrar/Archivar */}
              <div className="client-cell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={e => e.stopPropagation()}>
                <button
                  onClick={() => onArchive(op)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'rgba(239, 68, 68, 0.45)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 'var(--space-1)',
                    borderRadius: 'var(--radius-sm)',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'rgba(239, 68, 68, 0.45)'; e.currentTarget.style.background = 'transparent'; }}
                  title={t('table.archiveTitle')}
                  aria-label={t('table.archiveAriaLabel', { name: op.transaction || t('table.operationFallback') })}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
